import { combine, compute } from '@exodus/atoms'
import { createSelector } from 'reselect'
import { memoize } from 'lodash' // eslint-disable-line @exodus/restricted-imports/prefer-basic-utils -- TODO: fix next time we touch this file
import { mapValues } from '@exodus/basic-utils'
import { createDeepEqualOutputSelector } from '@exodus/core-selectors/utils/deep-equal'

const createAssetSourceActivityTxsSelector = memoize(
  ({ assetName, walletAccount }) =>
    createDeepEqualOutputSelector(
      (args) => args.txs,
      (args) => args.activityOptions,
      (args) => args.asset,
      (txs, activityOptions, asset) =>
        asset.api.getActivityTxs({
          txs,
          asset,
          options: activityOptions,
        })
    ),
  ({ assetName, walletAccount }) => `${assetName}_${walletAccount}`
)

const createAssetSourceActivityOptionsSelector = memoize(
  ({ assetName, walletAccount }) =>
    createDeepEqualOutputSelector(
      (args) => args.accountState,
      (args) => args.asset,
      (accountState, asset) =>
        asset.api.getActivityOptions({
          accountState,
        })
    ),
  ({ assetName, walletAccount }) => `${assetName}_${walletAccount}`
)

const createAssetSourceTxsSelector = memoize(
  ({ assetName, walletAccount }) =>
    createSelector(
      (args) => args.txSet,
      (txSet) => {
        return [...txSet]
      }
    ),
  ({ assetName, walletAccount }) => `${assetName}_${walletAccount}`
)

const areActivitiesEqual = (obj1 = {}, obj2 = {}) => {
  // Get wallet account keys from both objects
  const obj1WalletAccounts = Object.keys(obj1)
  const obj2WalletAccounts = Object.keys(obj2)

  // Check if both objects have the same number of wallet accounts
  if (obj1WalletAccounts.length !== obj2WalletAccounts.length) {
    return false
  }

  // Iterate over each wallet account in obj1
  for (const walletAccount of obj1WalletAccounts) {
    // Check if obj2 has the same wallet account
    if (!Object.prototype.hasOwnProperty.call(obj2, walletAccount)) {
      return false
    }

    const obj1Assets = obj1[walletAccount] || {}
    const obj2Assets = obj2[walletAccount] || {}

    const obj1AssetNames = Object.keys(obj1Assets)
    const obj2AssetNames = Object.keys(obj2Assets)

    if (obj1AssetNames.length !== obj2AssetNames.length) {
      return false
    }

    // Iterate over each asset in obj1Assets
    for (const assetName of obj1AssetNames) {
      // Check if obj2Assets has the same asset name and the data matches
      if (
        !Object.prototype.hasOwnProperty.call(obj2Assets, assetName) ||
        obj1Assets[assetName] !== obj2Assets[assetName]
      ) {
        return false
      }
    }
  }

  return true
}

const createActivityTxsAtom = ({ txLogsAtom, accountStatesAtom, assetsModule }) => {
  const inputAtom = combine({
    txLogs: txLogsAtom,
    accountStates: accountStatesAtom,
  })

  let prevResult

  const selector = ({ txLogs, accountStates }) => {
    const { value } = txLogs
    const { value: accountStatesValue } = accountStates
    const result = mapValues(value, (accountTxLogs, walletAccount) =>
      mapValues(accountTxLogs, (assetTxSet, assetName) => {
        const asset = assetsModule.getAsset(assetName)
        const baseAssetName = asset.baseAsset.name

        if (asset?.api?.getActivityTxs) {
          let activityOptions
          if (asset?.api?.getActivityOptions) {
            const baseAssetAccountState = accountStatesValue?.[walletAccount]?.[baseAssetName]

            activityOptions = createAssetSourceActivityOptionsSelector({
              assetName,
              walletAccount,
            })({
              accountState: baseAssetAccountState,
              asset,
            })
          }

          return createAssetSourceActivityTxsSelector({ assetName, walletAccount })({
            asset,
            txs: createAssetSourceTxsSelector({ assetName, walletAccount })({
              txSet: assetTxSet,
            }),
            activityOptions,
          })
        }

        return createAssetSourceTxsSelector({ assetName, walletAccount })({
          txSet: assetTxSet,
        })
      })
    )
    if (areActivitiesEqual(result, prevResult)) {
      return prevResult
    }

    prevResult = result

    return result
  }

  return compute({
    atom: inputAtom,
    selector,
  })
}

const activityTxsAtomDefinition = {
  id: 'activityTxsAtom',
  type: 'atom',
  factory: createActivityTxsAtom,
  dependencies: ['txLogsAtom', 'accountStatesAtom', 'assetsModule'],
  public: true,
}

export default activityTxsAtomDefinition
